import { useEffect, useRef, useState } from 'react';
import RecordRTC from 'recordrtc';
import type { RecordRTCOptions, RecordingState } from '../types';

export interface UseVideoRecorderOptions {
    /**
     * Passed straight through to `new RecordRTC(stream, options)`, merged over
     * the `{ type: 'video' }` default — full RecordRTC configuration (mimeType,
     * bitrate, timeSlice/ondataavailable for streaming, disableLogs, etc).
     */
    recordRTCOptions?: RecordRTCOptions;
    onRecordingComplete?: (blob: Blob, url: string) => void;
    /** Mirrors RecordRTC's own onStateChanged callback. */
    onStateChanged?: (state: RecordingState) => void;
}

/**
 * Owns the RecordRTC recording lifecycle: start/stop/pause/resume, the
 * resulting blob URL, and replaying/retrying against the live camera preview.
 *
 * The raw RecordRTC instance is returned as `recorder` while a recording is
 * active/paused, so anything not wrapped here (save, writeToDisk,
 * getDataURL, setRecordingDuration, getState, ...) is still reachable.
 * Note: calling lifecycle methods (e.g. `.destroy()`) directly on that raw
 * instance bypasses this hook's own state (`isRecording`/`recorder`) — only
 * `recordingState` stays accurate either way, since RecordRTC invokes
 * `onStateChanged` regardless of who called what.
 */
export const useVideoRecorder = (
    camera: MediaStream | null,
    videoRef: React.RefObject<HTMLVideoElement | null>,
    { recordRTCOptions, onRecordingComplete, onStateChanged }: UseVideoRecorderOptions = {},
) => {
    const [recorder, setRecorder] = useState<RecordRTC | null>(null);
    const [isRecording, setRecording] = useState(false);
    const [isRecorded, setRecorded] = useState(false);
    const [isPaused, setPaused] = useState(false);
    const [recordingState, setRecordingState] = useState<RecordingState>('inactive');
    const [recordURL, setRecordURL] = useState<string | null>(null);
    // RecordRTC picks the container/codec the current browser actually
    // supports (e.g. webm on Chrome/Firefox, often mp4 on Safari), so this
    // isn't knowable ahead of time.
    const [recordMimeType, setRecordMimeType] = useState<string | null>(null);

    const recordURLRef = useRef<string | null>(null);
    useEffect(() => {
        recordURLRef.current = recordURL;
    }, [recordURL]);

    useEffect(() => {
        return () => {
            if (recordURLRef.current) {
                URL.revokeObjectURL(recordURLRef.current);
            }
        }
    }, []);

    const startRecording = (overrideOptions?: RecordRTCOptions) => {
        if (!camera || !videoRef.current || isRecording) {
            return;
        }

        const tempRecorder = new RecordRTC(camera, {
            type: 'video',
            ...recordRTCOptions,
            ...overrideOptions,
        });

        tempRecorder.onStateChanged = (state) => {
            setRecordingState(state);
            onStateChanged?.(state);
        };

        videoRef.current.muted = true; // it could be set in in-line style, yet neat to write in here
        videoRef.current.volume = 0;

        tempRecorder.startRecording();

        setRecorder(tempRecorder);
        setRecording(true);
        setPaused(false);
    }

    const handleRecordingStopped = (finishedRecorder: RecordRTC) => {
        if (!videoRef.current) {
            return;
        }

        videoRef.current.srcObject = null;
        videoRef.current.src = '';
        videoRef.current.autoplay = false;
        videoRef.current.muted = false;
        videoRef.current.volume = 1;

        const blob = finishedRecorder.getBlob();
        const tempRecordURL = URL.createObjectURL(blob);
        videoRef.current.src = tempRecordURL;

        finishedRecorder.destroy();
        setRecorder(null);
        setRecordURL(tempRecordURL);
        setRecordMimeType(blob.type || null);
        setRecorded(true);
        setRecording(false);
        setPaused(false);
        onRecordingComplete?.(blob, tempRecordURL);
    }

    const stopRecording = () => {
        if (!recorder) {
            return;
        }
        recorder.stopRecording(() => handleRecordingStopped(recorder));
    }

    const pauseRecording = () => {
        if (!recorder) {
            return;
        }
        recorder.pauseRecording();
        setPaused(true);
    }

    const resumeRecording = () => {
        if (!recorder) {
            return;
        }
        recorder.resumeRecording();
        setPaused(false);
    }

    const reset = () => {
        recorder?.reset();
        if (recordURL) {
            URL.revokeObjectURL(recordURL);
        }
        setRecordURL(null);
        setRecordMimeType(null);
        setRecorded(false);
    }

    const retry = () => {
        if (!videoRef.current) {
            return;
        }

        if (recordURL) {
            URL.revokeObjectURL(recordURL);
        }

        videoRef.current.autoplay = true;
        videoRef.current.muted = true; // it could be set in in-line style, yet neat to write in here
        videoRef.current.volume = 0;
        videoRef.current.srcObject = camera;

        setRecordURL(null);
        setRecorded(false);
    }

    const play = () => {
        videoRef.current?.play();
    }

    return {
        recorder,
        isRecording,
        isRecorded,
        isPaused,
        recordingState,
        recordURL,
        recordMimeType,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        reset,
        retry,
        play,
    };
}
